import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@emotion/react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../button';
import { PopupMenu } from '../popup-menu';
import { ThemeContext } from '../theme';

const isTouchScreen = navigator.maxTouchPoints > 2 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);


const props = {
  className: PropTypes.string,
  hour: PropTypes.number,
  minute: PropTypes.number,
  seccond: PropTypes.number,
  timeFormat: PropTypes.oneOf(['12H', '24H']),
  timePeriod: PropTypes.oneOf(['AM', 'PM']),
  disableTimeFormat: PropTypes.bool,
  left: PropTypes.number,
  right: PropTypes.number,
  onChangeTime: PropTypes.func,
  isPristine: PropTypes.bool,
  noUnderline: PropTypes.bool,
  color: PropTypes.string,
  readOnlyMode: PropTypes.bool,
  autoOpen: PropTypes.bool
};

const defaultProps = {
  className: '',
  hour: 8,
  min: 20,
  left: 1,
  right: 0,
  timeFormat: '24H',
  disableTimeFormat: false,
  isPristine: false,
  noUnderline: false,
  readOnlyMode: false,
  autoOpen: false
};

const AutoOpenTimePicker = (props) => {
  const {
    hour: hourProp,
    minute: minuteProp,
    second: secondProp,
    timeFormat: timeFormatProp,
    timePeriod: timePeriodProp,
    left,
    right,
    disableTimeFormat,
    noUnderline,
    color
  } = props;

  // const [isMenuOpen, setIsMenuOpen] = useState(props.autoOpen || false);
  const [isMenuOpen, setIsMenuOpen] = useState(props.autoOpen);
  const [isPristine, setIsPristine] = useState(props.isPristine);
  const [hour, setHour] = useState(hourProp);
  const [minute, setMinute] = useState(minuteProp);
  const [second, setSecond] = useState(secondProp);
  const [timeFormat, setTimeFormat] = useState(timeFormatProp);
  const [timePeriod, setTimePeriod] = useState(timePeriodProp);
  const [isHourSelected, setIsHourSelected] = useState(false);
  const [isMinuteSelected, setIsMinuteSelected] = useState(false);

  const timeLabelParent = useRef();
  const activeMinRef = useRef();
  const activeHourRef = useRef();
  const context = useContext(ThemeContext);
  const theme = useTheme();

  let timePicker;
  if (theme && theme.timePicker) {
    timePicker = theme.timePicker;
  } else {
    timePicker = context.theme.timePicker;
  }

  useEffect(() => {
    setIsPristine(props.isPristine);
  }, [
    props.isPristine
  ]);

  useEffect(() => {
    setHour(props.hour);
    setMinute(props.minute);
    setIsMenuOpen(props.autoOpen);
  }, [
    props.hour,
    props.minute,
    props.autoOpen
  ]);

  const {
    labelColor,
    labelBorderColor,
    menuBgColor,
    menuTextColor,
    menuBorderColor,
    headerBgColor,
    headerTextColor,
    headerBorderColor,
    footerBgColor,
    footerTextColor,
    footerBorderColor,
    selectedBgColor,
    selectedTextColor,
    hoverBgColor,
    hoverTextColor
  } = timePicker;

  const styles = StyleSheet.create({
    timeLabelParent: {
      // width: timeFormat === '24H' ? '48px' : '71px',
      minWidth: '35px',
      height: '20px',
      position: 'relative'
    },
    timeLabel: {
      // width: timeFormat === '24H' ? '48px' : '71px',
      minWidth: '35px',
      height: '20px',
      color: color || labelColor,
      borderBottom: noUnderline ? '' : `1px solid ${labelBorderColor}`,
      cursor: 'pointer'
    },
    timePickerMenu: {
      width: disableTimeFormat ? '128px' : '192px',
      height: '372px',
      background: menuBgColor
    },
    timePickerMenuHeader: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4px',
      borderBottom: `1px solid ${headerBorderColor}`,
      background: headerBgColor,
      color: headerTextColor
    },
    headerCell: {
      width: '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    timeMenuContainer: {
      display: 'flex',
      minHeight: '292px',
      maxHeight: '290px'
    },
    timeColumn: {
      borderRight: `1px solid ${menuBorderColor}`,
      width: '64px',
      minWidth: '64px',
      maxWidth: '64px',
      overflowX: 'hidden',
      overflowY: 'auto'
    },
    timeCell: {
      height: '24px',
      width: '63px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      color: menuTextColor,
      // ':hover:not(.noHover)'
      ':hover:not(.selected)': {
        background: hoverBgColor,
        color: hoverTextColor
      }
    },
    activeCell: {
      background: selectedBgColor,
      color: selectedTextColor
    },
    menuFooter: {
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: '#0080FF',
      borderTop: `1px solid ${footerBorderColor}`,
      cursor: 'pointer',
      background: footerBgColor,
      footerTextColor: footerTextColor
    }
  });

  // useEffect(() => {
  //   if (isMenuOpen) {
  //     if (activeHourRef.current) {
  //       activeHourRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
  //     }
  //     setTimeout(() => {
  //       if (activeMinRef.current) {
  //         activeMinRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
  //       }
  //     }, 300);
  //   }
  // }, [isMenuOpen]);

  // useEffect(() => {
  //   if (activeHourRef && activeHourRef.current) {
  //     activeHourRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  //   }
  // }, [hour, timeFormat]);

  // --------------------------------- Event Handlers ---------------------------------
  const handleOnClickHour = (hour) => {
    setIsHourSelected(true);
    setHour(hour);
    // setIsPristine(false);
  };

  const handleOnClickMin = (minute) => {
    setIsMinuteSelected(true);
    setMinute(minute);
    // setIsPristine(false);
  };

  const handleOnClickOK = () => {
    setIsMenuOpen(false);
    setIsPristine(false);
    props.onChangeTime(hour, minute, timeFormat, timePeriod);
  };

  const handleOnCLickAMPM = () => {
    let updatedHour;
    let updatedTimePeriod;

    if (timeFormat === '24H') {
      if (hour === 0) {
        updatedHour = 12;
        updatedTimePeriod = 'AM';
      }

      if (hour === 12) {
        updatedHour = 12;
        updatedTimePeriod = 'PM';
      }

      if (hour > 12) {
        updatedHour = hour - 12;
        updatedTimePeriod = 'PM';
      }

      if (hour < 12) {
        updatedHour = hour;
        updatedTimePeriod = 'AM';
      }

      setHour(updatedHour);
      setTimeFormat('12H');
      setTimePeriod(updatedTimePeriod);
      // setIsPristine(false);
    }

    if (timeFormat === '12H' && timePeriod === 'AM') {
      setTimePeriod('PM');
      // setIsPristine(false);
    }

    if (timeFormat === '12H' && timePeriod === 'PM') {
      setTimePeriod('AM');
      // setIsPristine(false);
    }
  };

  const handleOnClick24H = () => {
    let updatedHour;

    if (timePeriod === 'AM') {
      if (hour === 12) {
        updatedHour = 0;
      } else {
        updatedHour = hour;
      }
    }

    if (timePeriod === 'PM') {
      if (hour === 12) {
        updatedHour = 12;
      } else {
        updatedHour = 12 + hour;
      }
    }

    setHour(updatedHour);
    setTimeFormat('24H');
  };

  const handleOutsideClick = () => {
    if (timeLabelParent && timeLabelParent.current) {
      setIsMenuOpen(false);
    }
  };

  const handleOnClickNow = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    setIsPristine(false);
    if (timeFormat === '12H') {
      let updatedHour = currentHour;
      let updatedTimePeriod = 'AM';

      if (currentHour === 0) {
        updatedHour = 12;
        updatedTimePeriod = 'AM';
      }

      if (currentHour === 12) {
        updatedHour = 12;
        updatedTimePeriod = 'PM';
      }

      if (currentHour > 12) {
        updatedHour = currentHour - 12;
        updatedTimePeriod = 'PM';
      }

      if (currentHour < 12) {
        updatedHour = currentHour;
        updatedTimePeriod = 'AM';
      }
      setHour(updatedHour);
      setMinute(currentMin);
      setTimePeriod(updatedTimePeriod);
      setIsMenuOpen(false);
      props.onChangeTime(hour, minute, timeFormat, timePeriod);
    } else {
      setHour(currentHour);
      setMinute(currentMin);
      setIsMenuOpen(false);
      props.onChangeTime(hour, minute, timeFormat, timePeriod);
    }
  };

  const hours = [];
  const mins = [];
  // const activeHour = isPristine ? hourProp : hour;
  // const activeMin = isPristine ? minuteProp : minute;
  // const activeTimeFormat = isPristine ? timeFormatProp : timeFormat;
  // const activeTimePeriod = isPristine ? timePeriodProp : timePeriod;

  // Generating hours menu
  if (timeFormat === '12H') {
    hours.push(
      <div
        key={uuidv4()}
        className={css(styles.timeCell, hour === 12 && styles.activeCell)}
        onClick={() => { handleOnClickHour(12); }}
      >
        12
      </div>
    );
    for (let i = 1; i < 12; i++) {
      hours.push(
        <div
          key={uuidv4()}
          className={css(styles.timeCell, i === hour && styles.activeCell)}
          onClick={() => { handleOnClickHour(i); }}
        >
          {i < 10 ? `0${i}` : i}
        </div>
      );
    }
  } else {
    for (let i = 0; i < 24; i++) {
      if (i === hour) {
        hours.push(
          <div
            key={uuidv4()}
            className={`${ isTouchScreen ? 'noHover' : ''} ${css(styles.timeCell, i === hour && styles.activeCell)} ${i === hour ? 'selected' : ''}`}
            onClick={() => { handleOnClickHour(i); }}
            ref={activeHourRef}
          >
            {i < 10 ? `0${i}` : i}
          </div>
        );
      } else {
        hours.push(
          <div
            key={uuidv4()}
            className={`${ isTouchScreen ? 'noHover' : ''} ${css(styles.timeCell, i === hour && styles.activeCell)} ${i === hour ? 'selected' : ''}`}
            onClick={() => { handleOnClickHour(i); }}
          >
            {i < 10 ? `0${i}` : i}
          </div>
        );
      }
    }
  }

  // Generating mins menu
  for (let i = 0; i < 60; i++) {
    if (i === minute) {
      mins.push(
        <div
          key={uuidv4()}
          className={`${ isTouchScreen ? 'noHover' : ''} ${css(styles.timeCell, i === minute && styles.activeCell)} ${i === minute ? 'selected' : ''}`}
          ref={activeMinRef}
          onClick={() => { handleOnClickMin(i); }}
        >
          {i < 10 ? `0${i}` : i}
        </div>
      );
    } else {
      mins.push(
        <div
          key={uuidv4()}
          className={`${ isTouchScreen ? 'noHover' : ''} ${css(styles.timeCell, i === minute && styles.activeCell)} ${i === minute ? 'selected' : ''}`}
          onClick={() => { handleOnClickMin(i); }}
        >
          {i < 10 ? `0${i}` : i}
        </div>
      );
    }
  }

  let timePickerTop = 0;
  if (timeLabelParent.current) {
    // console.log('🔥 ... TIME PICKER DEBUG🔥');
    // console.log(`window.innerHeight = ${window.innerHeight} and TimePicker top = ${timeLabelParent.current.getBoundingClientRect().top}`);
    // window.tp = timeLabelParent;
    if (window.innerHeight - timeLabelParent.current.getBoundingClientRect().top >= 372) { // was 400
      timePickerTop = 0;
    } else {
      timePickerTop = -372;
    }
  }

  return (
    <div
      className={`${props.className} ${css(styles.timeLabelParent)}`}
    >
      <div
        className={css(styles.timeLabel)}
        ref={timeLabelParent}
        onClick={() => {
          if (!props.readOnlyMode) {
            setIsMenuOpen(true);
            if ((props.timeFormat === '24H' && hours > -1 && hour < 24) || (props.timeFormat === '12H' && hours > 0 && hour < 13)) {
              setTimeout(() => {
                if (activeHourRef && activeHourRef.current) {
                  activeHourRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
                }
              }, 500);
            }
  
            if (minute > -1 && minute < 60) {
              setTimeout(() => {
                if (activeMinRef && activeMinRef.current) {
                  activeMinRef.current.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
                }
              }, 300);
            }
          }
        }}
      >
        {isPristine ? 'hh:mm' : `${hour < 10 ? '0' + hour : hour}:${minute < 10 ? '0' + minute : minute} ${timeFormat === '12H' ? timePeriod : ''}`}
      </div>
      {/* Time Menu */}
      <PopupMenu
        top={timePickerTop}
        left={left}
        right={right}
        position='absolute'
        visible={true}
        parent={timeLabelParent}
        onOutsideClick={handleOutsideClick}
      >
        <div className={css(styles.timePickerMenu)}>
          {/* Header */}
          <div className={css(styles.timePickerMenuHeader)}>
            {/* hh */}
            <div className={`${css(styles.headerCell)} noselect`} >
              hh
            </div>
            <div className={`${css(styles.headerCell)} noselect`} >
              mm
            </div>
            {!disableTimeFormat
              ? <div className={`${css(styles.headerCell)} noselect`} >
                  AM/PM
                </div>
              : null
            }
          </div>

          {/* Time picker columns */}
          <div className={css(styles.timeMenuContainer)}>
            {/* Hour column */}
            <div className={css(styles.timeColumn)}>
              {hours}
            </div>
            {/* Min column */}
            <div className={css(styles.timeColumn)}>
              {mins}
            </div>
            {/* AM/PM column */}
            {!disableTimeFormat
              ? <div
                  className={css(styles.timeColumn)}
                  style={{ width: '72px' }}
                >
                  <div
                    className={css(styles.timeCell, timeFormat === '12H' && timePeriod === 'AM' && styles.activeCell)}
                    onClick={handleOnCLickAMPM}
                  >
                    AM
                  </div>
                  <div
                    className={`noselect ${css(styles.timeCell, timeFormat === '12H' && timePeriod === 'PM' && styles.activeCell)}`}
                    onClick={handleOnCLickAMPM}
                  >
                    PM
                  </div>
                  <div
                    className={css(styles.timeCell, timeFormat === '24H' && styles.activeCell)}
                    onClick={handleOnClick24H}
                  >
                    24-Hour
                  </div>
                </div>
              : null
            }
          </div>

          {/* Footer */}
          <div className={`${css(styles.menuFooter)} noselect`}>
            <div
              className="ml-2"
              style={{ fontSize: '14px', visibility: 'hidden' }}
              onClick={handleOnClickNow}
            >
              NOW
            </div>

            <Button
              btnType="primary"
              onClick={handleOnClickOK}
              isDisabled={!isHourSelected || !isMinuteSelected}
              style={{
                width: disableTimeFormat ? '56px' : '80px',
                height: '30px',
                paddingTop: '3px',
                marginLeft: '8px',
                marginRight: '8px'
              }}
            >
              OK
            </Button>
          </div>
        </div>
      </PopupMenu>
    </div>
  );
};

AutoOpenTimePicker.propTypes = props;
AutoOpenTimePicker.defaultProps = defaultProps;

export { AutoOpenTimePicker };
